import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';

interface IntegrationSettings {
  enabled: boolean;
  apiUrl: string;
  apiKey: string;
  sendDelayMinutes: number;
  retryEnabled: boolean;
  retryMaxAttempts: number;
  sendBio: boolean;
  allowedJobTypes: string[];
}

const AVAILABLE_JOB_TYPES = [
  { value: 'warranty_claim', label: 'Warranty Claims' },
  { value: 'service_call', label: 'Service Calls' },
  { value: 'installation', label: 'Installations' },
  { value: 'repair', label: 'Repairs' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'inspection', label: 'Inspections' },
  { value: 'emergency', label: 'Emergency Calls' },
];

export default function IntegrationsPage() {
  const [settings, setSettings] = useState<IntegrationSettings>({
    enabled: false,
    apiUrl: '',
    apiKey: '',
    sendDelayMinutes: 60,
    retryEnabled: true,
    retryMaxAttempts: 3,
    sendBio: false,
    allowedJobTypes: ['warranty_claim', 'service_call', 'installation', 'repair', 'maintenance']
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [queue, setQueue] = useState<any[]>([]);

  useEffect(() => {
    loadSettings();
    loadLogs();
    loadQueue();

    // Real-time subscription for logs
    const logsChannel = supabase
      ?.channel('integration_logs_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'integration_logs'
      }, () => {
        loadLogs();
      })
      .subscribe();

    return () => {
      logsChannel?.unsubscribe();
    };
  }, []);

  async function loadSettings() {
    const client = supabase;
    if (!client) {
      console.warn('Supabase client not configured');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await client
        .from('integration_settings')
        .select('key, value');

      if (error) {
        console.error('Error loading settings:', error);
        return;
      }

      if (data) {
        const settingsMap = data.reduce((acc: Record<string, string>, item: any) => {
          acc[item.key] = item.value;
          return acc;
        }, {});

        const allowedJobTypes = settingsMap['rahsreviews_allowed_job_types'] 
          ? JSON.parse(settingsMap['rahsreviews_allowed_job_types'])
          : ['warranty_claim', 'service_call', 'installation', 'repair', 'maintenance'];

        setSettings({
          enabled: settingsMap['rahsreviews_enabled'] === 'true',
          apiUrl: settingsMap['rahsreviews_api_url'] || '',
          apiKey: '',
          sendDelayMinutes: parseInt(settingsMap['rahsreviews_send_delay_minutes'] || '60'),
          retryEnabled: settingsMap['rahsreviews_retry_enabled'] === 'true',
          retryMaxAttempts: parseInt(settingsMap['rahsreviews_retry_max_attempts'] || '3'),
          sendBio: settingsMap['rahsreviews_send_bio'] === 'true',
          allowedJobTypes: allowedJobTypes
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadLogs() {
    const client = supabase;
    if (!client) {
      console.warn('Supabase client not configured');
      return;
    }

    const { data, error } = await client
      .from('integration_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error loading logs:', error);
      return;
    }

    if (data) setLogs(data);
  }

  async function loadQueue() {
    const client = supabase;
    if (!client) {
      console.warn('Supabase client not configured');
      return;
    }

    const { data, error } = await client
      .from('review_request_queue')
      .select('*')
      .order('scheduled_at', { ascending: true })
      .limit(50);

    if (error) {
      console.error('Error loading queue:', error);
      return;
    }

    if (data) setQueue(data);
  }

  async function saveSettings() {
    setSaving(true);
    try {
      const client = supabase;
      if (!client) {
        throw new Error('Supabase client not configured');
      }

      const updates = [
        { key: 'rahsreviews_enabled', value: String(settings.enabled) },
        { key: 'rahsreviews_api_url', value: settings.apiUrl },
        { key: 'rahsreviews_send_delay_minutes', value: String(settings.sendDelayMinutes) },
        { key: 'rahsreviews_retry_enabled', value: String(settings.retryEnabled) },
        { key: 'rahsreviews_retry_max_attempts', value: String(settings.retryMaxAttempts) },
        { key: 'rahsreviews_send_bio', value: String(settings.sendBio) },
        { key: 'rahsreviews_allowed_job_types', value: JSON.stringify(settings.allowedJobTypes) }
      ];

      const { error } = await client.from('integration_settings').upsert(updates, { onConflict: 'key' });

      if (error) {
        throw error;
      }

      if (settings.apiKey) {
        alert('⚠️ Remember to set RAHSREVIEWS_API_KEY in Supabase secrets');
      }

      alert('✅ Settings saved!');
    } catch (error: any) {
      alert(`❌ Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function runWorkerNow() {
    try {
      const client = supabase;
      if (!client) {
        throw new Error('Supabase client not configured');
      }

      const { data, error } = await client.functions.invoke('reviews-worker');

      if (error) throw error;

      alert(`✅ Worker ran: ${data?.results?.successful || 0} successful`);
      loadLogs();
      loadQueue();
    } catch (error: any) {
      alert(`❌ Error: ${error.message}`);
    }
  }

  function toggleJobType(jobType: string) {
    setSettings(prev => ({
      ...prev,
      allowedJobTypes: prev.allowedJobTypes.includes(jobType)
        ? prev.allowedJobTypes.filter(t => t !== jobType)
        : [...prev.allowedJobTypes, jobType]
    }));
  }

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold">RahsReviews Integration</h1>
        <p className="text-gray-600 mt-2">
          Automatically send review requests when claims are completed
        </p>
      </div>

      {/* Settings Card */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Configuration</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="font-medium">Enable Integration</label>
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              className="h-4 w-4"
            />
          </div>

          <div>
            <label className="block font-medium mb-2">API URL</label>
            <Input
              value={settings.apiUrl}
              onChange={(e) => setSettings({ ...settings, apiUrl: e.target.value })}
              placeholder="https://rahs-reviews.com/api"
            />
          </div>

          <div>
            <label className="block font-medium mb-2">API Key</label>
            <Input
              type="password"
              value={settings.apiKey}
              onChange={(e) => setSettings({ ...settings, apiKey: e.target.value })}
              placeholder="Enter API key"
            />
            <p className="text-sm text-gray-500 mt-1">
              Set RAHSREVIEWS_API_KEY in Supabase secrets
            </p>
          </div>

          <div>
            <label className="block font-medium mb-2">Delay (minutes)</label>
            <Input
              type="number"
              value={settings.sendDelayMinutes}
              onChange={(e) => setSettings({ ...settings, sendDelayMinutes: parseInt(e.target.value) || 60 })}
              min="0"
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="font-medium">Enable Retry</label>
            <input
              type="checkbox"
              checked={settings.retryEnabled}
              onChange={(e) => setSettings({ ...settings, retryEnabled: e.target.checked })}
              className="h-4 w-4"
            />
          </div>

          {settings.retryEnabled && (
            <div>
              <label className="block font-medium mb-2">Max Retry Attempts</label>
              <Input
                type="number"
                value={settings.retryMaxAttempts}
                onChange={(e) => setSettings({ ...settings, retryMaxAttempts: parseInt(e.target.value) || 3 })}
                min="1"
                max="10"
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <label className="font-medium">Send Technician Bio</label>
            <input
              type="checkbox"
              checked={settings.sendBio}
              onChange={(e) => setSettings({ ...settings, sendBio: e.target.checked })}
              className="h-4 w-4"
            />
          </div>

          {/* Job Type Filtering */}
          <div className="border-t pt-4">
            <label className="block font-medium mb-3">Allowed Job Types</label>
            <p className="text-sm text-gray-500 mb-3">
              Select which job types should trigger review requests
            </p>
            <div className="grid grid-cols-2 gap-3">
              {AVAILABLE_JOB_TYPES.map((jobType) => (
                <label
                  key={jobType.value}
                  className="flex items-center space-x-2 p-3 border rounded cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={settings.allowedJobTypes.includes(jobType.value)}
                    onChange={() => toggleJobType(jobType.value)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{jobType.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {settings.allowedJobTypes.length} of {AVAILABLE_JOB_TYPES.length} selected
            </p>
          </div>

          <div className="flex gap-4 pt-4">
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button onClick={runWorkerNow} variant="outline">
              Run Worker Now
            </Button>
          </div>
        </div>
      </Card>

      {/* Queue Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Review Request Queue</h2>
          <Button onClick={loadQueue} variant="outline" size="sm">Refresh</Button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Scheduled At</th>
                <th className="text-left p-2">Claim ID</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Attempts</th>
                <th className="text-left p-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {queue.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center p-4 text-gray-500">
                    No pending requests
                  </td>
                </tr>
              ) : (
                queue.map((item) => (
                  <tr key={item.id} className="border-b">
                    <td className="p-2">{new Date(item.scheduled_at).toLocaleString()}</td>
                    <td className="p-2 font-mono text-sm">{item.claim_id.substring(0, 8)}...</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        item.status === 'completed' ? 'bg-green-100' :
                        item.status === 'failed' ? 'bg-red-100' :
                        item.status === 'processing' ? 'bg-blue-100' :
                        'bg-yellow-100'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="p-2">{item.attempts}</td>
                    <td className="p-2 text-sm text-red-600">{item.last_error || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Logs Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Integration Logs</h2>
          <Button onClick={loadLogs} variant="outline" size="sm">Refresh</Button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Time</th>
                <th className="text-left p-2">Action</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Claim ID</th>
                <th className="text-left p-2">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center p-4 text-gray-500">
                    No logs yet
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="border-b">
                    <td className="p-2 text-sm">{new Date(log.created_at).toLocaleString()}</td>
                    <td className="p-2">{log.action}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        log.status === 'success' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="p-2 font-mono text-xs">{log.claim_id?.substring(0, 8) || '-'}</td>
                    <td className="p-2 text-sm text-red-600">{log.error_message || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
